import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserContext = createContext(null);

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');
      if (token && savedUser) return JSON.parse(savedUser);
    } catch (e) {
      /* ignore corrupt storage */
    }
    return null;
  });
  const [loading, setLoading] = useState(false);

  // Keep the context user in sync with localStorage whenever login/logout/role-switch
  // happens elsewhere (so the mobile drawer & tab bar update immediately, no restart).
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    const syncFromStorage = () => {
      const t = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');
      if (t && savedUser) {
        try {
          setUser(JSON.parse(savedUser));
          axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
        } catch (e) {
          console.error('Error parsing saved user:', e);
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          setUser(null);
        }
      } else {
        setUser(null);
        delete axios.defaults.headers.common['Authorization'];
      }
    };

    window.addEventListener('userUpdated', syncFromStorage);
    window.addEventListener('storage', syncFromStorage);
    window.addEventListener('gj:logout', syncFromStorage);
    return () => {
      window.removeEventListener('userUpdated', syncFromStorage);
      window.removeEventListener('storage', syncFromStorage);
      window.removeEventListener('gj:logout', syncFromStorage);
    };
  }, []);

  const updateUser = useCallback((newUserData) => {
    setUser(newUserData);
    if (newUserData) {
      localStorage.setItem('user', JSON.stringify(newUserData));
      // Dispatch custom event to notify App.js
      window.dispatchEvent(new Event('userUpdated'));
    }
  }, []);

  const login = useCallback((userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  }, []);

  const switchRole = useCallback(async (newRole) => {
    try {
      const response = await axios.put(`${API}/user/switch-role?new_role=${newRole}`);
      updateUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Error switching role:', error);
      throw error;
    }
  }, [updateUser]);

  const value = {
    user,
    loading,
    updateUser,
    login,
    logout,
    switchRole,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export default UserContext;
